import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "./ui/carousel";
import { TrendingUp, Clock, Target, Users, Star, Quote, Building2 } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";

export function ClientResults() {
  const kpis = [
    {
      icon: Clock,
      value: "30%",
      label: "Faster Settlements",
      description: "Reduced processing time"
    },
    {
      icon: Target,
      value: "98%",
      label: "Accuracy in Reconciliation",
      description: "Error reduction rate"
    },
    {
      icon: TrendingUp,
      value: "25%",
      label: "ROI Improvement",
      description: "Average client uplift"
    },
    {
      icon: Users,
      value: "150+",
      label: "Enterprise Clients",
      description: "Trusted by retailers"
    }
  ];

  const testimonials = [
    {
      id: 1,
      name: "David Martinez",
      title: "VP of Procurement",
      company: "TechnoMart Solutions",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face",
      quote: "NexProcureAI has completely transformed our procurement strategy. The predictive analytics helped us reduce inventory costs by 32% while maintaining 99.5% service levels. The ROI was visible within 6 months.",
      rating: 5,
      results: "32% cost reduction, 99.5% service level"
    },
    {
      id: 2,
      name: "Amanda Chen",
      title: "Chief Operations Officer",
      company: "Metro Retail Network",
      image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=400&fit=crop&crop=face",
      quote: "The scheme management capabilities are phenomenal. We've automated 90% of our promotional workflows and eliminated manual errors completely. Our teams can now focus on strategic initiatives rather than administrative tasks.",
      rating: 5,
      results: "90% workflow automation, 0% manual errors"
    },
    {
      id: 3,
      name: "Robert Kim",
      title: "Director of Supply Chain",
      company: "FreshMart Enterprises",
      image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop&crop=face",
      quote: "The AI-powered auction platform revolutionized our supplier negotiations. We achieved 18% better pricing on average and reduced procurement cycle time by 45%. The transparency and analytics are unmatched.",
      rating: 5,
      results: "18% better pricing, 45% faster cycles"
    }
  ];

  return (
    <section className="bg-white py-16 lg:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-12 max-w-3xl text-center">
          <Badge variant="secondary" className="mb-4">
            <TrendingUp className="mr-1 h-3 w-3" />
            Proven Results
          </Badge>
          <h2 className="mb-4 text-3xl lg:text-4xl text-black font-bold">Client Results & Impact</h2>
          <p className="text-lg text-muted-foreground">
            See the measurable impact NexProcureAI delivers for retail enterprises worldwide.
          </p>
        </div>
        
        <div className="mb-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {kpis.map((kpi, index) => (
            <Card key={index} className="text-center" style={{ backgroundColor: '#E8DDDA' }}>
              <CardHeader className="pb-2">
                <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-white">
                  <kpi.icon className="h-6 w-6 text-secondary" />
                </div>
                <CardTitle className="text-3xl text-black">{kpi.value}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-medium">{kpi.label}</p>
                <p className="text-sm text-muted-foreground">{kpi.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Testimonials Carousel */}
        <div className="mx-auto max-w-5xl">
          <div className="text-center mb-8">
            <h3 className="text-2xl lg:text-3xl mb-4">What Our Clients Say</h3>
            <p className="text-muted-foreground">
              Real stories from procurement leaders who've transformed their operations
            </p>
          </div>

          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {testimonials.map((testimonial) => (
                <CarouselItem key={testimonial.id} className="pl-4 md:basis-1/2 lg:basis-1/1">
                  <Card className="h-full bg-secondary border-secondary/20 hover:shadow-lg transition-all duration-300">
                    <CardContent className="p-8">
                      <div className="flex flex-col h-full">
                        {/* Quote Icon and Stars */}
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20">
                            <Quote className="h-6 w-6 text-white" />
                          </div>
                          <div className="flex gap-1">
                            {Array.from({ length: testimonial.rating }).map((_, i) => (
                              <Star key={i} className="h-4 w-4 fill-white text-white" />
                            ))}
                          </div>
                        </div>

                        {/* Quote */}
                        <blockquote className="text-lg mb-6 flex-grow text-white">
                          "{testimonial.quote}"
                        </blockquote>

                        {/* Results Badge */}
                        <div className="mb-6">
                          <Badge variant="secondary" className="text-xs bg-white/20 text-white border-white/30 hover:bg-white/30">
                            <Building2 className="mr-1 h-3 w-3" />
                            {testimonial.results}
                          </Badge>
                        </div>

                        {/* Author Info */}
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <div className="h-12 w-12 rounded-full overflow-hidden bg-white/20">
                              <ImageWithFallback
                                src={testimonial.image}
                                alt={testimonial.name}
                                className="h-full w-full object-cover"
                              />
                            </div>
                            <div className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-white border-2 border-secondary"></div>
                          </div>
                          <div>
                            <div className="font-semibold text-white">{testimonial.name}</div>
                            <div className="text-sm text-white/80">{testimonial.title}</div>
                            <div className="text-sm font-medium text-white">{testimonial.company}</div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="flex justify-center mt-8 gap-2">
              <CarouselPrevious className="relative translate-x-0 translate-y-0" />
              <CarouselNext className="relative translate-x-0 translate-y-0" />
            </div>
          </Carousel>
        </div>
      </div>
    </section>
  );
}